import * as THREE from '@phdsoft/three';
import { GraphicsMaterials } from '../digital-twin/graphics/graphics-materials';
import type { InspectionPointInstanceManager } from '../digital-twin/graphics/inspection-point-instance-manager.service';
import { GaugingElement } from '../platform/busines-data/SectionElements/InspectionElements/gaugingElement';

/** False for an instanced POINT, whose SurfaceDesign is a PhantomSurfaceDesign (no Object3D, no geometry). */
function isRealMesh(inspection: GaugingElement | null | undefined): boolean {
  return inspection?.SurfaceDesign instanceof THREE.Object3D;
}

/**
 * POINT preview of the retarget tool, two kinds:
 * - `mesh`: the picked inspection's own mesh shows the point built for the clicked spot (`built`:
 *   pose + shape), keeping its blue "picked" paint; the fields hold its original pose.
 * - `overlay`: the inspection's mesh is not visible (its region was hidden by a cross-region switch),
 *   so `built` itself is added to the scene, painted blue; the real mesh is left untouched.
 *   An instanced POINT (phantom SurfaceDesign) always uses this kind: its instance is hidden while
 *   the preview shows (`phantomVisible` holds the phantom's prior `visible`); pose and batch untouched.
 */
interface PointPreview {
  kind: 'mesh' | 'overlay';
  inspection: GaugingElement;
  built: THREE.Mesh;
  position?: THREE.Vector3; rotation?: THREE.Euler; scale?: THREE.Vector3;
  geometry?: THREE.BufferGeometry;
  phantomVisible?: boolean;
}

/** Owns at most one POINT preview; `take()` hands the built point over, `remove()` disposes its shape. */
export class RetargetPointPreview {
  private current: PointPreview | null = null;

  constructor(private scene: () => THREE.Object3D | null | undefined) {}

  /** Inspection whose point is previewed, or null. */
  public get inspection(): GaugingElement | null { return this.current?.inspection ?? null; }

  /** Kind of the current preview, or null. */
  public get kind(): 'mesh' | 'overlay' | null { return this.current?.kind ?? null; }

  /**
   * Shows `built` for `inspection` (a previous preview must have been taken/removed first).
   * With `overlay` false the real mesh takes the built pose and shape (same section group,
   * world == local like the loader and the drag tool); with `overlay` true `built` is added to the
   * scene with the blue point material (mesh kind as a fallback when there is no scene).
   * An instanced POINT is always an overlay and its instance is hidden meanwhile (`phantom.visible`);
   * without a scene nothing is shown and the instance is left alone.
   */
  public show(inspection: GaugingElement, built: THREE.Mesh, overlay: boolean): void {
    const phantom = !isRealMesh(inspection);
    const scene = overlay || phantom ? this.scene() : null;
    if (scene) {
      built.material = GraphicsMaterials.pointMaterialBlue;
      built.raycast = () => {}; // never picked, whatever raycasts the scene (only its pose/geometry is taken)
      scene.add(built);
      this.current = { kind: 'overlay', inspection, built };
      if (phantom) {
        this.current.phantomVisible = inspection.SurfaceDesign.visible;
        inspection.SurfaceDesign.visible = false; // the original disappears, the blue point shows the new spot
      }
      return;
    }
    if (phantom) {
      this.current = { kind: 'overlay', inspection, built }; // never the phantom: it has no geometry
      return;
    }
    const mesh = inspection.SurfaceDesign;
    this.current = {
      kind: 'mesh', inspection, built,
      position: mesh.position.clone(), rotation: mesh.rotation.clone(), scale: mesh.scale.clone(),
      geometry: mesh.geometry as THREE.BufferGeometry,
    };
    mesh.position.copy(built.position);
    mesh.rotation.copy(built.rotation);
    mesh.scale.copy(built.scale);
    mesh.geometry = built.geometry;
  }

  /**
   * Ends the preview without disposing: an overlay leaves the scene, a mesh preview puts the
   * original pose and shape back. Returns the built point (the caller owns its geometry).
   */
  public take(): THREE.Mesh | null {
    const p = this.current;
    if (!p) return null;
    this.current = null;
    if (p.kind === 'overlay') {
      p.built.parent?.remove(p.built);
      if (p.phantomVisible !== undefined) p.inspection.SurfaceDesign.visible = p.phantomVisible;
      return p.built;
    }
    const mesh = p.inspection.SurfaceDesign;
    if (mesh.geometry === p.built.geometry) mesh.geometry = p.geometry;
    mesh.position.copy(p.position);
    mesh.rotation.copy(p.rotation);
    mesh.scale.copy(p.scale);
    return p.built;
  }

  /** Ends the preview and disposes the built shape (once: a second call is a no-op). */
  public remove(): void {
    this.take()?.geometry.dispose();
  }
}

/**
 * Translucent version of the picked-point blue (same colour, side and polygon offset as
 * `GraphicsMaterials.pointMaterialBlue`): reads as "where the point is now", distinct from the solid
 * blue preview. Shared by every ghost and never disposed.
 */
const ORIGIN_GHOST_MATERIAL = new THREE.MeshBasicMaterial({
  name: 'RetargetOriginGhost',
  color: GraphicsMaterials.pointMaterialBlue.color.getHex(),
  side: GraphicsMaterials.pointMaterialBlue.side,
  polygonOffset: true,
  polygonOffsetFactor: GraphicsMaterials.pointMaterialBlue.polygonOffsetFactor,
  polygonOffsetUnits: GraphicsMaterials.pointMaterialBlue.polygonOffsetUnits,
  transparent: true,
  opacity: 0.45,
  depthWrite: false,
});

/**
 * Ghost marker of the picked inspection's ORIGINAL pose, shown while the tool shows another region
 * (the inspection's own region, and so its real mesh, is hidden then). A scene-level mesh that shares
 * the real mesh's geometry (never cloned, never disposed here) and copies its world transform; the
 * real mesh and region visibility are never touched. Not pickable (`raycast` is a no-op).
 * An instanced POINT has no mesh: the ghost shares its batch's shape geometry (the
 * PointShapeGeometryCache entry, never disposed) at its slot's world matrix (the saved pose while the
 * instance is hidden, e.g. by the point preview).
 */
export class RetargetOriginGhost {
  private ghost: THREE.Mesh | null = null;

  constructor(
    private scene: () => THREE.Object3D | null | undefined,
    private pointManager: () => InspectionPointInstanceManager | null | undefined = () => null,
  ) {}

  /** (Re)creates the ghost at `inspection`'s current world pose (call while it holds its original pose). */
  public show(inspection: GaugingElement): void {
    this.remove();
    const scene = this.scene();
    const ghost = scene ? (isRealMesh(inspection) ? this.meshGhost(inspection) : this.instanceGhost(inspection)) : null;
    if (!ghost) return;
    ghost.name = 'RetargetOriginGhost';
    ghost.renderOrder = 999; // drawn after the other transparent objects
    ghost.raycast = () => {}; // never picked, whatever raycasts the scene
    scene.add(ghost);
    this.ghost = ghost;
  }

  /** Detaches the ghost (the shared geometry and material are left alone); a no-op without one. */
  public remove(): void {
    this.ghost?.parent?.remove(this.ghost);
    this.ghost = null;
  }

  private meshGhost(inspection: GaugingElement): THREE.Mesh | null {
    const real = inspection.SurfaceDesign;
    if (!real?.geometry) return null;
    real.updateWorldMatrix(true, false);
    const ghost = new THREE.Mesh(real.geometry, ORIGIN_GHOST_MATERIAL);
    real.matrixWorld.decompose(ghost.position, ghost.quaternion, ghost.scale);
    return ghost;
  }

  private instanceGhost(inspection: GaugingElement): THREE.Mesh | null {
    const manager = this.pointManager();
    // the batch draws with PointShapeGeometryCache.get(shapeType) (buildBatch/addInstance): shared, never disposed
    const geometry = manager?.getInstanceInfo(inspection)?.mesh.geometry;
    const matrix = new THREE.Matrix4();
    if (!geometry || !manager.getInstanceWorldMatrix(inspection, matrix)) return null;
    const ghost = new THREE.Mesh(geometry, ORIGIN_GHOST_MATERIAL);
    ghost.matrixAutoUpdate = false;
    ghost.matrix.copy(matrix);
    ghost.matrixWorldNeedsUpdate = true;
    return ghost;
  }
}

/**
 * Green of `GraphicsMaterials.greenMaterial`, pulled towards the camera (polygon offset -4) so it wins
 * over the merged lightsection mesh drawn at the same place, and never written to depth. Shared by
 * every target highlight and never disposed.
 */
const TARGET_HIGHLIGHT_MATERIAL: THREE.Material = (() => {
  const material = GraphicsMaterials.greenMaterial.clone();
  material.name = 'RetargetTargetHighlight';
  material.polygonOffset = true;
  material.polygonOffsetFactor = -4;
  material.polygonOffsetUnits = -4;
  material.depthWrite = false;
  material.transparent = true;
  return material;
})();

/**
 * Green highlight of the picked target when its own mesh is hidden: in REGIONS on a new-GLTF asset
 * the detailed element meshes are hidden and each section shows one merged lightsection mesh, so the
 * material swap on the target's mesh shows nothing. A scene-level mesh that shares the target mesh's
 * geometry (never cloned, never disposed here) at its world matrix; the target mesh and the merged
 * mesh are never touched. Not pickable (`raycast` is a no-op).
 */
export class RetargetTargetHighlight {
  private overlay: THREE.Mesh | null = null;

  constructor(private scene: () => THREE.Object3D | null | undefined) {}

  /** The current overlay, or null. */
  public get mesh(): THREE.Mesh | null { return this.overlay; }

  /** (Re)creates the overlay over `source` (the target's detailed mesh); false when there is none. */
  public show(source: THREE.Mesh | null | undefined): boolean {
    this.remove();
    const scene = this.scene();
    if (!scene || !source?.geometry) return false;
    source.updateWorldMatrix(true, false);
    const overlay = new THREE.Mesh(source.geometry, TARGET_HIGHLIGHT_MATERIAL);
    overlay.name = 'RetargetTargetHighlight';
    overlay.matrixAutoUpdate = false;
    overlay.matrix.copy(source.matrixWorld);
    overlay.matrixWorldNeedsUpdate = true;
    overlay.renderOrder = 2;
    overlay.userData.retargetPreview = true; // a tool mesh: never a target
    overlay.raycast = () => {}; // never picked, whatever raycasts the scene
    scene.add(overlay);
    this.overlay = overlay;
    return true;
  }

  /** Detaches the overlay (the shared geometry and material are left alone); a no-op without one. */
  public remove(): void {
    this.overlay?.parent?.remove(this.overlay);
    this.overlay = null;
  }
}
